/**
 * CihLandingHero — centered marketing hero for CIH index/landing pages
 * (/coaching-intelligence, /coaching-business, /coaching-business/playbooks).
 *
 * Soft accent-tinted wash + concentric rings behind a centered eyebrow, title,
 * lede, and optional CTA row. `children` renders below the CTAs (stat rows,
 * anchor chips). Accent picks the vertical's color: violet for the hub,
 * green for the Coach Business vertical. An optional `breadcrumb` sits at the
 * top-left of the content column, in the same place as on CihHero detail
 * pages, and takes the place of the hero's extra top padding.
 *
 * Aliases: landing hero, cih landing hero, marketing hero
 */

import { motion } from "motion/react";
import type { ReactNode } from "react";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { cn } from "~/lib/cn";
import { useReducedMotionSafe } from "~/lib/hooks/use-reduced-motion-safe";

type Accent = "violet" | "green";

const ACCENTS: Record<
  Accent,
  { eyebrow: string; blobA: string; blobB: string; rings: string }
> = {
  violet: {
    eyebrow: "text-violet-700",
    blobA: "bg-violet-200/50",
    blobB: "bg-purple-100/70",
    rings: "text-violet-300",
  },
  green: {
    eyebrow: "text-green-700",
    blobA: "bg-emerald-200/40",
    blobB: "bg-teal-100/60",
    rings: "text-emerald-300",
  },
};

type BreadcrumbItem = { label: string; href?: string };

type Props = {
  readonly breadcrumb?: BreadcrumbItem[];
  readonly eyebrow: string;
  readonly title: ReactNode;
  readonly lede: string;
  readonly accent?: Accent;
  readonly cta?: ReactNode;
  readonly children?: ReactNode;
};

export function CihLandingHero({
  breadcrumb,
  eyebrow,
  title,
  lede,
  accent = "violet",
  cta,
  children,
}: Props) {
  const reducedMotion = useReducedMotionSafe();
  const colors = ACCENTS[accent];

  return (
    <section className="relative overflow-hidden border-b border-gray-200/80 bg-white">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className={`absolute top-0 left-1/2 h-[480px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl ${colors.blobA}`}
        />
        <div
          className={`absolute -top-24 left-[12%] h-72 w-72 rounded-full blur-3xl ${colors.blobB}`}
        />
        <svg
          viewBox="0 0 1200 420"
          fill="none"
          preserveAspectRatio="xMidYMax slice"
          className={`absolute bottom-0 left-1/2 h-[420px] w-[1200px] -translate-x-1/2 ${colors.rings}`}
        >
          <g stroke="currentColor">
            <circle cx="600" cy="560" r="260" strokeOpacity="0.5" />
            <circle cx="600" cy="560" r="380" strokeOpacity="0.35" />
            <circle cx="600" cy="560" r="500" strokeOpacity="0.2" />
            <circle cx="600" cy="560" r="620" strokeOpacity="0.1" />
          </g>
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        {breadcrumb ? (
          <Breadcrumb
            items={breadcrumb}
            isBold={false}
            className="pt-6 md:pt-8"
          />
        ) : null}
        <motion.div
          initial={reducedMotion ? false : { opacity: 0, y: 18 }}
          animate={reducedMotion ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={cn("text-center", breadcrumb ? "pt-6" : "pt-16 md:pt-24")}
        >
          <div
            className={`text-xs font-medium tracking-[0.22em] uppercase ${colors.eyebrow}`}
          >
            ● {eyebrow}
          </div>
          <h1 className="mx-auto mt-5 max-w-4xl font-heading text-4xl font-medium tracking-tight text-balance text-gray-900 md:text-6xl xl:text-7xl">
            {title}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-normal text-balance text-gray-600">
            {lede}
          </p>
          {cta ? (
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              {cta}
            </div>
          ) : null}
          {children}
        </motion.div>
      </div>
    </section>
  );
}
